import { useMemo } from "react";

import { MultiSelect } from "../../../../common/Atoms/MultiSelect";
import { InlineSelect } from "../../../../common/Atoms/InlineSelect";
import { useInvoice } from "../../../hooks/useInvoice";
import { useInvoiceTags } from "../../../hooks/useInvoiceTags";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import { TagBadge } from "../../Molecules/TagBadge";
import { useApplyInvoiceTagMutation, useClearInvoiceTagsMutation } from "../../../services/api/invoiceTagsApi/invoiceTags";

/** `inline` renders the borderless version used in the invoice details card */
export function InvoiceTagSelector({ inline }: { inline?: boolean } = {}) {
  const { invoice } = useInvoice();
  const currentOrg = useGetSelectedOrganisation();
  const [applyTag, { isLoading: applyLoading }] = useApplyInvoiceTagMutation();
  const [removeTag, { isLoading: removeLoading }] = useClearInvoiceTagsMutation();
  const { tags, colorMap } = useInvoiceTags();

  const invoiceTags: string[] = invoice?.tags || [];

  const displayValue = useMemo(() => {
    if (!invoiceTags.length) {
      return `No tags applied`;
    }

    if (invoiceTags.length === 1) {
      return (
        <TagBadge
          name={ invoiceTags[0] }
          color={ colorMap[invoiceTags[0]] }
          className={ `mr-2` }
        />
      );
    }

    return (
      <span className={ `flex items-center` }>
        <TagBadge
          name={ invoiceTags[0] }
          color={ colorMap[invoiceTags[0]] }
          className={ `mr-2` }
        />
        <span
          className={ `italic text-gray-500` }
        >
          { ` + ${invoiceTags.length - 1} more` }
        </span>
      </span>
    );
  }, [invoiceTags, colorMap]);

  if (!invoice || !currentOrg) {
    return null;
  }

  function onChange(newValue: string[]) {
    if (newValue.length < invoiceTags.length) {
      // A tag was removed
      const removedTag = invoiceTags.find(tag => !newValue.includes(tag));

      removeTag({
        organisationId: currentOrg.id,
        invoiceIds: [invoice.id],
        tagName: removedTag,
      });

      return;
    }

    const addedTag = newValue.find(tag => !invoiceTags.includes(tag));

    applyTag({
      organisationId: currentOrg.id,
      invoiceIds: [invoice.id],
      tag: addedTag,
    });
  }

  if (inline) {
    return (
      <InlineSelect
        multiple
        hideChevron
        options={ tags.map(tag => ({ value: tag, label: tag })) }
        value={ invoiceTags }
        onChange={ onChange }
        disabled={ applyLoading || removeLoading }
        display={ invoiceTags.length ? (
          <span className={ `flex flex-wrap items-center gap-1` }>
            { invoiceTags.map(tag => (
              <TagBadge
                key={ tag }
                name={ tag }
                color={ colorMap[tag] }
              />
            )) }
          </span>
        ) : (
          <span className={ `text-gray-500` }>{ `+ Add tag` }</span>
        ) }
      />
    );
  }

  return (
    <MultiSelect
      size={ `sm` }
      onChange={ onChange }
      options={ tags.map(tag => ({ value: tag, label: tag })) }
      selected={ invoiceTags }
      display={ displayValue }
      loading={ applyLoading || removeLoading }
      listButtonClassname={ invoiceTags.length && `py-0.5` } // Account for badge height
      containerClassname={ `min-h-0` }
    />
  );
}
